import { useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  TextInput,
  Group,
  Loader,
  Center,
  Alert,
  Image,
  Card,
  SegmentedControl,
  Select,
  Divider,
  NumberInput,
  Box,
  NativeSelect,
  UnstyledButton,
  Checkbox,
  ActionIcon,
  Badge,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconX,
  IconPhoto,
  IconArrowRight,
  IconArrowLeft,
  IconCube,
  IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useMutation } from '@tanstack/react-query';
import { reviewsService } from '../../services/reviews';
import { productsService } from '../../services/products';
import { HashtagTextarea } from '../../components/hashtags/HashtagTextarea';
import { tagsService } from '../../services/tags';
import { StarRating } from '../../components/reviews';
import { ReviewVisibility } from '../../types';
import type { ReviewTag, FindByNameResult } from '../../types';

const PURCHASE_SOURCES = ['Amazon', 'Walmart', 'Target', 'Costco', 'eBay', 'In-store', 'Brand website', 'Other'];

const CURRENCIES = [
  { code: 'USD', symbol: '$' }, { code: 'EUR', symbol: '€' }, { code: 'GBP', symbol: '£' },
  { code: 'CAD', symbol: 'C$' }, { code: 'AUD', symbol: 'A$' }, { code: 'JPY', symbol: '¥' },
  { code: 'INR', symbol: '₹' }, { code: 'BRL', symbol: 'R$' }, { code: 'MXN', symbol: 'MX$' },
  { code: 'KRW', symbol: '₩' }, { code: 'CNY', symbol: '¥' }, { code: 'CHF', symbol: 'Fr' },
];

const REGION_CURRENCY: Record<string, string> = {
  US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', JP: 'JPY', IN: 'INR',
  BR: 'BRL', MX: 'MXN', KR: 'KRW', CN: 'CNY', CH: 'CHF',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR',
  AT: 'EUR', PT: 'EUR', FI: 'EUR', IE: 'EUR', GR: 'EUR',
};

function detectCurrency(): string {
  try {
    const region = new Intl.Locale(navigator.language).region ?? '';
    return REGION_CURRENCY[region] ?? 'USD';
  } catch {
    return 'USD';
  }
}

const SCALE_COLORS = ['red', 'yellow', 'teal'] as const;

function ScaleSelector({
  group,
  tags,
  value,
  onChange,
}: {
  group: string;
  tags: ReviewTag[];
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  return (
    <Box>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={6}>{group}</Text>
      <Group gap={0} style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--mantine-color-default-border)' }}>
        {tags.map((tag, i) => {
          const selected = value === String(tag.id);
          const color = SCALE_COLORS[i] ?? 'gray';
          return (
            <UnstyledButton
              key={tag.id}
              onClick={() => onChange(selected ? null : String(tag.id))}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px 8px',
                borderLeft: i > 0 ? '1px solid var(--mantine-color-default-border)' : undefined,
                backgroundColor: selected
                  ? `var(--mantine-color-${color}-light)`
                  : `var(--mantine-color-gray-0)`,
                transition: 'background-color 120ms',
              }}
            >
              <Text size="sm" c={selected ? 'dark' : 'dimmed'} fw={selected ? 600 : 400}>
                {tag.emoji} {tag.label}
              </Text>
            </UnstyledButton>
          );
        })}
      </Group>
    </Box>
  );
}

function VisibilityLabel({ title, sub }: { title: string; sub: string }) {
  return (
    <Stack gap={0} align="center">
      <Text size="sm" fw={600} lh={1.3}>{title}</Text>
      <Text size="xs" c="dimmed" lh={1.3}>{sub}</Text>
    </Stack>
  );
}

type Step = 1 | 2 | 3;

export default function CreateReview() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productIdParam = searchParams.get('productId');

  const hasProductParam = !!productIdParam;

  // Step wizard — skip to step 3 when a product is already known
  const [step, setStep] = useState<Step>(() => hasProductParam ? 3 : 1);

  // Step 1 & 2: product search
  const [searchName, setSearchName] = useState('');
  const [searchBrand, setSearchBrand] = useState('');
  const [searchResults, setSearchResults] = useState<FindByNameResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Selected product
  const [selectedProductId, setSelectedProductId] = useState<string | null>(productIdParam ?? null);
  const [selectedProductInfo, setSelectedProductInfo] = useState<{ name: string; brand?: string; imageUrl?: string } | null>(null);

  const { data: product, isLoading: isLoadingProduct, isError: productError } = useQuery({
    queryKey: ['product', productIdParam],
    queryFn: () => productsService.getById(productIdParam!),
    enabled: !!productIdParam,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['review-tags'],
    queryFn: () => tagsService.getAll(),
    staleTime: Infinity,
  });

  // Review fields
  const [rating, setRating] = useState(0);
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<string>(String(ReviewVisibility.Public));
  const [valueTagId, setValueTagId] = useState<string | null>(null);
  const [qualityTagId, setQualityTagId] = useState<string | null>(null);
  const [experienceTagId, setExperienceTagId] = useState<string | null>(null);
  const [purchaseSource, setPurchaseSource] = useState<string | null>(null);
  const [customSource, setCustomSource] = useState('');
  const [purchasePrice, setPurchasePrice] = useState<number | ''>('');
  const [purchaseCurrency, setPurchaseCurrency] = useState(() => detectCurrency());
  const [isPromotional, setIsPromotional] = useState(false);
  const [imageItems, setImageItems] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tagGroups = allTags.reduce<Record<string, typeof allTags>>((acc, tag) => {
    if (!acc[tag.group]) acc[tag.group] = [];
    acc[tag.group].push(tag);
    return acc;
  }, {});

  // ── Step navigation ──────────────────────────────────────────────────────────
  const handleContinueToStep2 = async () => {
    if (!searchName.trim()) return;
    setStep(2);
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const query = searchBrand.trim() ? `${searchBrand.trim()} ${searchName.trim()}` : searchName.trim();
      const response = await productsService.findByName(query);
      const combined = [...response.localResults, ...response.externalResults].slice(0, 10);
      setSearchResults(combined);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectProduct = (result: FindByNameResult) => {
    setSelectedProductId(result.id);
    setSelectedProductInfo({ name: result.name, brand: result.brand, imageUrl: result.imageUrl });
    setStep(3);
  };

  const handleNoneOfThese = () => {
    setSelectedProductId(null);
    setSelectedProductInfo(null);
    setStep(3);
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const submitMutation = useMutation({
    mutationFn: () => {
      const payload = selectedProductId
        ? {
            productId: selectedProductId,
            rating,
            content: content.trim(),
            visibility: Number(visibility) as ReviewVisibility,
            tagIds: [valueTagId, qualityTagId, experienceTagId].filter(Boolean).map(Number),
            purchaseSource: purchaseSource === 'Other' ? (customSource.trim() || null) : (purchaseSource || null),
            purchasePrice: purchasePrice === '' ? null : purchasePrice,
            purchaseCurrency: purchasePrice !== '' ? purchaseCurrency : undefined,
            isPromotional,
          }
        : {
            product: {
              name: searchName.trim(),
              brand: searchBrand.trim() || undefined,
            },
            rating,
            content: content.trim(),
            visibility: Number(visibility) as ReviewVisibility,
            tagIds: [valueTagId, qualityTagId, experienceTagId].filter(Boolean).map(Number),
            purchaseSource: purchaseSource === 'Other' ? (customSource.trim() || null) : (purchaseSource || null),
            purchasePrice: purchasePrice === '' ? null : purchasePrice,
            purchaseCurrency: purchasePrice !== '' ? purchaseCurrency : undefined,
            isPromotional,
          };
      return reviewsService.create(payload);
    },
    onSuccess: async (created) => {
      if (imageItems.length > 0) {
        try {
          await reviewsService.uploadImages(created.id, imageItems.map((i) => i.file));
        } catch {
          notifications.show({ title: 'Warning', message: 'Review posted, but some images failed to upload', color: 'yellow' });
          navigate(`/reviews/${created.id}`);
          return;
        }
      }
      notifications.show({ title: 'Posted!', message: 'Your review has been published', color: 'green' });
      navigate(`/reviews/${created.id}`);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to post review. Please try again.', color: 'red' });
    },
  });

  const handleSubmit = () => {
    if (rating === 0) {
      notifications.show({ title: 'Error', message: 'Please select a rating', color: 'red' });
      return;
    }
    if (!content.trim()) {
      notifications.show({ title: 'Error', message: 'Please write your review', color: 'red' });
      return;
    }
    if (!selectedProductId && !searchName.trim()) {
      notifications.show({ title: 'Error', message: 'Please go back and enter the product name', color: 'red' });
      return;
    }
    submitMutation.mutate();
  };

  if (isLoadingProduct) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (productIdParam && (productError || !product)) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Product not found
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Alert>
      </Container>
    );
  }

  // ── Step indicator ───────────────────────────────────────────────────────────
  const StepIndicator = ({ current }: { current: Step }) => (
    <Group gap="xs" mb="md">
      {([1, 2, 3] as Step[]).map((s) => (
        <Group key={s} gap={4}>
          <Box
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: s === current
                ? 'var(--mantine-color-brand-filled)'
                : s < current
                ? 'var(--mantine-color-teal-6)'
                : 'var(--mantine-color-gray-3)',
              color: s <= current ? 'white' : 'var(--mantine-color-gray-6)',
              fontSize: 12,
              fontWeight: 700,
              transition: 'background-color 200ms',
            }}
          >
            {s}
          </Box>
          {s < 3 && (
            <Box style={{ width: 24, height: 2, backgroundColor: s < current ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-gray-3)', borderRadius: 1 }} />
          )}
        </Group>
      ))}
      <Text size="sm" c="dimmed" ml={4}>Step {current} of 3</Text>
    </Group>
  );

  // ── Step 1: What are you reviewing? ─────────────────────────────────────────
  if (step === 1) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <StepIndicator current={1} />
        <Title order={2} mb={4}>What are you reviewing?</Title>
        <Text c="dimmed" mb="xl">Enter the product name and we'll help you find it.</Text>

        <Stack gap="md">
          <TextInput
            label="Brand"
            description="Optional"
            placeholder="e.g. Apple, Nike, Kellogg's"
            value={searchBrand}
            onChange={(e) => setSearchBrand(e.currentTarget.value)}
            maxLength={100}
          />
          <TextInput
            label="Product name"
            placeholder={searchBrand.trim() ? `e.g. ${searchBrand.trim()} MacBook Pro` : 'e.g. iPhone 15 Pro, Air Max 90'}
            required
            value={searchName}
            onChange={(e) => setSearchName(e.currentTarget.value)}
            maxLength={256}
            onKeyDown={(e) => { if (e.key === 'Enter' && searchName.trim()) handleContinueToStep2(); }}
          />

          <Group justify="space-between" mt="sm">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button
              rightSection={<IconArrowRight size={16} />}
              disabled={!searchName.trim()}
              onClick={handleContinueToStep2}
            >
              Continue
            </Button>
          </Group>
        </Stack>
      </Container>
    );
  }

  // ── Step 2: Is it one of these? ─────────────────────────────────────────────
  if (step === 2) {
    const searchLabel = searchBrand.trim() ? `${searchBrand.trim()} ${searchName.trim()}` : searchName.trim();

    return (
      <Container size="sm" pt="md" pb="xl">
        <StepIndicator current={2} />
        <Title order={2} mb={4}>Is it one of these?</Title>
        <Text c="dimmed" mb="xl">Searching for "{searchLabel}"</Text>

        {searchLoading ? (
          <Center py={60}>
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text c="dimmed" size="sm">Searching Amazon and our database…</Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="xs">
            {searchResults.map((result) => (
              <UnstyledButton
                key={result.id}
                onClick={() => handleSelectProduct(result)}
                style={{ borderRadius: 12, display: 'block' }}
              >
                <Card
                  withBorder
                  radius="md"
                  p="sm"
                  style={{ transition: 'border-color 150ms, background-color 150ms', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--mantine-color-brand-filled)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--mantine-color-default-border)'; }}
                >
                  <Group gap="sm" wrap="nowrap">
                    {result.imageUrl ? (
                      <Image
                        src={result.imageUrl}
                        w={64}
                        h={64}
                        radius="sm"
                        fit="contain"
                        style={{ flexShrink: 0 }}
                      />
                    ) : (
                      <ThemeIcon size={64} radius="sm" variant="light" color="gray">
                        <IconCube size={28} />
                      </ThemeIcon>
                    )}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} lineClamp={2} size="sm">{result.name}</Text>
                      {result.brand && <Text size="xs" c="dimmed">{result.brand}</Text>}
                      {result.category && <Text size="xs" c="dimmed">{result.category}</Text>}
                      <Group gap="xs" mt={4} wrap="wrap">
                        {result.reviewCount > 0 && (
                          <Text size="xs" c="dimmed">
                            {result.reviewCount} review{result.reviewCount !== 1 ? 's' : ''}
                            {result.averageRating ? ` · ${result.averageRating.toFixed(1)}★` : ''}
                          </Text>
                        )}
                        {result.asin && (
                          <Badge size="xs" variant="light" color="orange">Amazon</Badge>
                        )}
                      </Group>
                    </Box>
                    <IconChevronRight size={18} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0 }} />
                  </Group>
                </Card>
              </UnstyledButton>
            ))}

            <Button
              variant="default"
              mt="xs"
              rightSection={<IconArrowRight size={16} />}
              onClick={handleNoneOfThese}
            >
              {searchResults.length === 0 ? 'No matches found — continue anyway' : 'None of these'}
            </Button>

            <Group justify="flex-start" mt="xs">
              <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => setStep(1)}>
                Back
              </Button>
            </Group>
          </Stack>
        )}
      </Container>
    );
  }

  // ── Step 3: Write your review ────────────────────────────────────────────────
  const displayProduct = product ?? (selectedProductInfo ? { ...selectedProductInfo } : null);

  return (
    <Container size="sm" pt="md" pb="xl">
      {!hasProductParam && <StepIndicator current={3} />}
      <Title order={2} mb="lg">Write a Review</Title>

      {/* Product banner */}
      {displayProduct && (
        <Card shadow="sm" padding="md" radius="md" withBorder mb="lg">
          <Group gap="sm" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              {displayProduct.imageUrl ? (
                <Image
                  src={displayProduct.imageUrl}
                  alt={displayProduct.name}
                  w={56}
                  h={56}
                  radius="sm"
                  fit="contain"
                  style={{ flexShrink: 0 }}
                />
              ) : (
                <ThemeIcon size={56} radius="sm" variant="light" color="gray" style={{ flexShrink: 0 }}>
                  <IconCube size={24} />
                </ThemeIcon>
              )}
              <Box style={{ minWidth: 0 }}>
                <Text fw={500} lineClamp={1}>{displayProduct.name}</Text>
                {displayProduct.brand && <Text size="sm" c="dimmed">{displayProduct.brand}</Text>}
              </Box>
            </Group>
            {!hasProductParam && (
              <Button variant="subtle" size="xs" color="gray" onClick={() => setStep(1)} style={{ flexShrink: 0 }}>
                Change
              </Button>
            )}
          </Group>
        </Card>
      )}

      <Stack gap="md">
        <div>
          <Text size="sm" fw={500} mb="xs">
            Rating <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span>
          </Text>
          <StarRating rating={rating} onChange={setRating} readonly={false} size="lg" />
        </div>

        <HashtagTextarea
          value={content}
          onChange={setContent}
          placeholder="Share your thoughts about this product..."
          required
          minRows={5}
          maxRows={15}
          maxLength={5000}
        />

        {/* Image picker */}
        <div>
          <Text size="sm" fw={500} mb="xs">
            Photos <Text span c="dimmed" size="xs">(optional, up to 5)</Text>
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.currentTarget.files ?? []);
              const remaining = 5 - imageItems.length;
              const newItems = files.slice(0, remaining).map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
              setImageItems((prev) => [...prev, ...newItems]);
              e.currentTarget.value = '';
            }}
          />
          <Group gap="xs" wrap="wrap">
            {imageItems.map((item, i) => (
              <div key={item.preview} style={{ position: 'relative' }}>
                <img
                  src={item.preview}
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--mantine-color-default-border)', display: 'block' }}
                  alt=""
                />
                <ActionIcon
                  size="xs"
                  color="red"
                  variant="filled"
                  radius="xl"
                  style={{ position: 'absolute', top: -6, right: -6 }}
                  onClick={() => {
                    URL.revokeObjectURL(item.preview);
                    setImageItems((prev) => prev.filter((_, idx) => idx !== i));
                  }}
                >
                  <IconX size={10} />
                </ActionIcon>
              </div>
            ))}
            {imageItems.length < 5 && (
              <UnstyledButton
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  border: '2px dashed var(--mantine-color-default-border)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  color: 'var(--mantine-color-dimmed)',
                  transition: 'border-color 150ms, color 150ms',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mantine-color-brand-filled)';
                  e.currentTarget.style.color = 'var(--mantine-color-brand-filled)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mantine-color-default-border)';
                  e.currentTarget.style.color = 'var(--mantine-color-dimmed)';
                }}
              >
                <IconPhoto size={20} />
                <Text size="xs">Add photo</Text>
              </UnstyledButton>
            )}
          </Group>
        </div>

        <Divider label="Optional details" labelPosition="left" />

        {allTags.length > 0 && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>Quick ratings <Text span c="dimmed" size="xs">(optional)</Text></Text>
            <ScaleSelector group="Value"      tags={tagGroups['Value']      ?? []} value={valueTagId}      onChange={setValueTagId} />
            <ScaleSelector group="Quality"    tags={tagGroups['Quality']    ?? []} value={qualityTagId}    onChange={setQualityTagId} />
            <ScaleSelector group="Experience" tags={tagGroups['Experience'] ?? []} value={experienceTagId} onChange={setExperienceTagId} />
          </Stack>
        )}

        <Stack gap="xs">
          <Select
            label="Where did you buy it?"
            placeholder="Select retailer (optional)"
            value={purchaseSource}
            onChange={(v) => { setPurchaseSource(v); if (v !== 'Other') setCustomSource(''); }}
            clearable
            data={PURCHASE_SOURCES}
          />
          {purchaseSource === 'Other' && (
            <TextInput
              placeholder="Enter retailer name"
              value={customSource}
              onChange={(e) => setCustomSource(e.currentTarget.value)}
              maxLength={50}
            />
          )}
          <div>
            <Text size="sm" fw={500} mb={4}>
              What did you pay? <Text span size="sm" c="dimmed">(optional)</Text>
            </Text>
            <Group gap={4} wrap="nowrap" align="flex-start">
              <NativeSelect
                value={purchaseCurrency}
                onChange={(e) => setPurchaseCurrency(e.currentTarget.value)}
                data={CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} ${c.symbol}` }))}
                style={{ width: 100, flexShrink: 0 }}
              />
              <NumberInput
                style={{ flex: 1 }}
                placeholder="0.00"
                value={purchasePrice}
                onChange={(val) => setPurchasePrice(val === '' ? '' : Number(val))}
                decimalScale={2}
                min={0}
                max={99999}
              />
            </Group>
          </div>
        </Stack>

        <Checkbox
          label="I received this product for free or at a discount in exchange for my review"
          checked={isPromotional}
          onChange={(e) => setIsPromotional(e.currentTarget.checked)}
        />

        <div>
          <Text size="sm" fw={500} mb={6}>Visibility</Text>
          <SegmentedControl
            fullWidth
            value={visibility}
            onChange={setVisibility}
            data={[
              { value: String(ReviewVisibility.Public),        label: <VisibilityLabel title="Public"    sub="Everyone" /> },
              { value: String(ReviewVisibility.FollowersOnly), label: <VisibilityLabel title="Followers" sub="Followers only" /> },
              { value: String(ReviewVisibility.Private),       label: <VisibilityLabel title="Private"   sub="Only you" /> },
            ]}
          />
        </div>

        <Group justify="space-between" mt="md">
          {!hasProductParam ? (
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => setStep(2)}>
              Back
            </Button>
          ) : (
            <Button variant="subtle" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSubmit} loading={submitMutation.isPending}>
            Post Review
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
